import { getAllWithoutPagination } from "@/utils/graphql";
import { Injectable } from "@angular/core";
import { BehaviorSubject, from, Observable, of } from "rxjs";
import { map, switchMap, tap, catchError } from "rxjs/operators";
import { EditOnboardingElementsGQL, GetOnboardingElementIfExistsGQL, OnboardingElementFragment, OnboardingElementsGQL } from "../graphql/graphql";

@Injectable({
  providedIn: 'root',
})
export class OnboardingDeprecatedService {
  private readonly _loadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly _loadingObservable$: Observable<boolean> = this._loadingSubject.asObservable();

  private readonly _elementsSubject: BehaviorSubject<OnboardingElementFragment[]> = new BehaviorSubject<OnboardingElementFragment[]>([]);
  private readonly _elements$: Observable<OnboardingElementFragment[]> = this._elementsSubject.asObservable();

  private _elementsWereFetched = false;

  public constructor(
    private readonly _featuredElementsGql: OnboardingElementsGQL,
    private readonly _editFeaturedElementsGql: EditOnboardingElementsGQL,
    private readonly _getElementIfExistsGql: GetOnboardingElementIfExistsGQL,
  ) { }

  public getFeaturedElements(): Observable<OnboardingElementFragment[]> {
    if (this._elementsWereFetched) {
      return this._elements$;
    }

    return from(this.fetchElements()).pipe(
      tap((elements) => {
        this._elementsSubject.next(elements);
        this._elementsWereFetched = true;
      }),
      switchMap(() => this._elements$),
    );
  }

  public updateFeaturedIds(values: number[]): void {
    this._loadingSubject.next(true);

    this._editFeaturedElementsGql.mutate({ input: { elementIds: values } })
      .toPromise()
      .then((elements) => {
        this._loadingSubject.next(false);
        this._elementsSubject.next(elements.data?.element?.editOnboarding?.items ?? []);
      });
  }

  public addFeaturedElement(id: number): void {
    const ids: number[] = this._elementsSubject.value.map(element => element.id);
    if (!ids.includes(id)) {
      ids.unshift(id);
      this.updateFeaturedIds(ids);
    }
  }

  public removeFeaturedElement(id: number): void {
    const ids: number[] = this._elementsSubject.value.map(element => element.id);
    const index = ids.findIndex(next => next === id);
    ids.splice(index, 1);
    this.updateFeaturedIds(ids);
  }

  private async fetchElements(): Promise<OnboardingElementFragment[]> {
    this._loadingSubject.next(true);

    const result = await getAllWithoutPagination(
      (x) => this._featuredElementsGql
        .fetch({ meta: { pageCursor: x, pageSize: 199 } })
        .toPromise(), (res) => res.data.elements,
    );

    this._loadingSubject.next(false);
    return result;
  }

  public getElementIfExists(id: number): Observable<OnboardingElementFragment | null> {
    this._loadingSubject.next(true);

    return this._getElementIfExistsGql.fetch({ id })
      .pipe(
        map((x) => {
          this._loadingSubject.next(false);
          return x.data.element;
        }),
        catchError(err => {
          this._loadingSubject.next(false);
          return of(null);
        }),
      );
  }
}
